import { useSelectedShop } from '../ShopContext'
import { useEarnings, formatMoney, type EarningsBucket } from '../useEarnings'

// Earnings snapshot — three big cards (Today / This week / This month)
// showing revenue + order count per bucket. Server aggregates in UTC;
// buckets are last-24h/last-7d/last-30d rolling windows (not calendar
// today/week/month — kept simple for v0.1). Currency formatting via
// Intl.NumberFormat, falls back to $ for unknown codes.
//
// Refunded orders excluded from revenue on the server side (see
// shopEarningsSummaryHandler). Order count still includes refunds — the
// merchant should see the volume even when the net was zeroed out.
// (v0.2: revisit if this creates a "3 orders but $0" confusion pattern.)

export function EarningsScreen() {
  const shop = useSelectedShop()
  const { state, refresh } = useEarnings(shop?.handle ?? null)

  // No shop yet — same treatment as OrdersScreen. Merchant needs the
  // Settings tab to sign out; Earnings shouldn't render a broken shell.
  if (!shop) {
    return (
      <div style={s.wrap}>
        <div style={s.eyebrow}>Earnings</div>
        <div style={s.placeholder}>
          No shop selected yet. Create your first shop at
          {' '}<strong>headlo.com</strong>{' '}
          — earnings totals show up here once orders start rolling in.
        </div>
      </div>
    )
  }

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <div style={s.headerText}>
          <div style={s.eyebrow}>Earnings</div>
          <div style={s.shopNameRow}>
            <h1 style={s.shopName}>{shop?.brand_name || shop?.handle || 'Earnings'}</h1>
            {shop?.has_shopify && <span style={s.shopifyBadge}>Shopify</span>}
          </div>
        </div>
        <button
          style={s.refreshBtn}
          onClick={refresh}
          disabled={state.status === 'loading' || (state.status === 'ready' && state.refreshing)}
          aria-label="Refresh"
          title="Refresh"
        >
          {state.status === 'loading' || (state.status === 'ready' && state.refreshing) ? '…' : '↻'}
        </button>
      </div>

      {state.status === 'loading' && (
        <div style={s.placeholder}>Loading earnings…</div>
      )}

      {state.status === 'error' && (
        <div style={s.errorCard}>
          <div style={s.errorTitle}>Couldn't load earnings</div>
          <div style={s.errorMsg}>{state.message}</div>
          <button style={s.retryBtn} onClick={refresh}>Try again</button>
        </div>
      )}

      {state.status === 'ready' && (
        <div style={s.cards}>
          <BucketCard label="Last 24 hours" bucket={state.summary.buckets.today} currency={state.summary.currency} accent="green" />
          <BucketCard label="Last 7 days"   bucket={state.summary.buckets.week}  currency={state.summary.currency} />
          <BucketCard label="Last 30 days"  bucket={state.summary.buckets.month} currency={state.summary.currency} />
        </div>
      )}

      {state.status === 'ready' && (
        <div style={s.foot}>
          {state.summary.has_shopify
            ? 'Totals pulled from Shopify (synced hourly).'
            : 'Totals from your Headlo checkout, refunds excluded.'}
        </div>
      )}
    </div>
  )
}

// ── BucketCard ─────────────────────────────────────────────────────────────

function BucketCard({
  label, bucket, currency, accent,
}: {
  label:    string
  bucket:   EarningsBucket
  currency: string
  accent?:  'green'
}) {
  const isZero = bucket.count === 0 && bucket.cents === 0
  return (
    <div style={{ ...s.card, ...(accent === 'green' ? s.cardAccent : {}) }}>
      <div style={{ ...s.cardLabel, ...(accent === 'green' ? s.cardLabelAccent : {}) }}>{label}</div>
      <div style={{ ...s.cardValue, ...(accent === 'green' ? s.cardValueAccent : {}) }}>
        {formatMoney(bucket.cents, currency)}
      </div>
      <div style={{ ...s.cardSub, ...(accent === 'green' ? s.cardSubAccent : {}) }}>
        {isZero
          ? 'No orders'
          : `${bucket.count} ${bucket.count === 1 ? 'order' : 'orders'}`}
      </div>
    </div>
  )
}

const s = {
  wrap: {
    padding: '24px 20px calc(24px + env(safe-area-inset-bottom))',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 24,
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  eyebrow: {
    fontSize: 11,
    fontWeight: 700,
    color: '#8a8a80',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  shopNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
  },
  shopName: {
    fontSize: 28,
    fontWeight: 700,
    marginTop: 0,
    marginBottom: 0,
    color: '#1a1a18',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    minWidth: 0,
  },
  shopifyBadge: {
    padding: '2px 8px',
    background: '#95bf47',
    color: '#ffffff',
    borderRadius: 4,
    fontSize: 10,
    fontFamily: 'inherit',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.3,
    flexShrink: 0,
  },
  refreshBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    background: '#ffffff',
    color: '#1a1a18',
    border: '1px solid #e5e5e0',
    cursor: 'pointer',
    fontSize: 18,
    fontFamily: 'inherit',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  cards: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 12,
  },
  card: {
    padding: '20px 22px',
    background: '#ffffff',
    border: '1px solid #e5e5e0',
    borderRadius: 14,
  },
  cardAccent: {
    background: '#2a6a3a',
    border: '1px solid #2a6a3a',
  },
  cardLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: '#8a8a80',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    marginBottom: 8,
  },
  cardLabelAccent: {
    color: 'rgba(250,249,246,0.7)',
  },
  cardValue: {
    fontSize: 34,
    fontWeight: 700,
    color: '#1a1a18',
    fontVariantNumeric: 'tabular-nums' as const,
    lineHeight: 1.1,
    marginBottom: 6,
  },
  cardValueAccent: {
    color: '#faf9f6',
  },
  cardSub: {
    fontSize: 13,
    color: '#5a5a55',
  },
  cardSubAccent: {
    color: 'rgba(250,249,246,0.85)',
  },

  foot: {
    marginTop: 20,
    padding: '0 4px',
    fontSize: 12,
    color: '#8a8a80',
    lineHeight: 1.5,
    fontStyle: 'italic' as const,
  },

  placeholder: {
    padding: 20,
    background: '#f0efe9',
    borderRadius: 12,
    fontSize: 14,
    color: '#5a5a55',
    textAlign: 'center' as const,
  },
  errorCard: {
    padding: 20,
    background: '#3a1e1e',
    color: '#f5b7b7',
    borderRadius: 12,
  },
  errorTitle: {
    fontSize: 14,
    fontWeight: 700,
    marginBottom: 6,
  },
  errorMsg: {
    fontSize: 12,
    marginBottom: 12,
    fontFamily: '"SF Mono", Consolas, monospace',
    opacity: 0.9,
  },
  retryBtn: {
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 600,
    background: '#f5b7b7',
    color: '#3a1e1e',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
} as const
